import React from 'react';
import { ArrowLeft, Shield, Heart, AlertOctagon, Flag, CheckCircle2, MessageCircle, Sparkles } from 'lucide-react';

interface CommunityGuidelinesViewProps {
  onBack?: () => void;
  onRequestReport?: () => void;
}

export const CommunityGuidelinesView: React.FC<CommunityGuidelinesViewProps> = ({
  onBack,
  onRequestReport,
}) => {
  return (
    <div className="max-w-4xl mx-auto py-8 sm:py-12 px-4 sm:px-8 text-[#FAFAFA]">
      {onBack && (
        <button
          type="button"
          onClick={onBack}
          className="inline-flex items-center gap-2 mb-6 px-3 py-1.5 rounded-xl border border-[#262626] bg-[#141414] text-xs font-semibold text-[#D4D4D4] hover:bg-[#1C1C1C] transition-colors shadow-xs"
        >
          <ArrowLeft className="w-4 h-4" />
          <span>Back to Quick!Bible</span>
        </button>
      )}

      {/* Header Banner */}
      <div className="rounded-3xl bg-[#141414] border border-[#262626] text-white p-6 sm:p-10 mb-8 shadow-md">
        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#1C1C1C] text-amber-300 text-xs font-bold uppercase tracking-wider mb-3">
          <Shield className="w-4 h-4" />
          <span>Christian Fellowship Standards</span>
        </div>
        <h1 className="text-2xl sm:text-4xl font-extrabold tracking-tight mb-3">
          Quick!Bible Community Guidelines
        </h1>
        <p className="text-sm sm:text-base text-[#FAFAFA] max-w-2xl leading-relaxed">
          Quick!Bible is a faith-centered global Christian platform dedicated to exploring God’s Holy Word in joy, unity, and mutual encouragement. These guidelines keep our community safe, edifying, and welcoming to believers of all ages.
        </p>
      </div>

      <div className="space-y-8 bg-[#141414] p-6 sm:p-10 rounded-3xl border border-[#262626] shadow-xs">
        {/* Guiding Scripture */}
        <div className="p-4 sm:p-6 rounded-2xl bg-amber-500/10 border border-amber-500/20 text-amber-200">
          <div className="flex items-start gap-3">
            <Heart className="w-5 h-5 text-amber-500 shrink-0 mt-0.5" />
            <div>
              <blockquote className="italic font-serif text-sm sm:text-base leading-relaxed">
                “Let no corrupting talk come out of your mouths, but only such as is good for building up, as fits the occasion, that it may give grace to those who hear.”
              </blockquote>
              <cite className="block text-xs font-mono font-semibold text-amber-400 mt-2">
                — Ephesians 4:29 (ESV / KJV Canonical Public Domain)
              </cite>
            </div>
          </div>
        </div>

        {/* Section 1 */}
        <section className="space-y-3">
          <h2 className="text-lg sm:text-xl font-bold text-[#FAFAFA] flex items-center gap-2.5">
            <CheckCircle2 className="w-5 h-5 text-emerald-500 shrink-0" />
            <span>1. Faith-Centered, Respectful Fellowship</span>
          </h2>
          <p className="text-sm text-[#D4D4D4] leading-relaxed">
            Treat every player, competitor, host, and scholar with Christian love, grace, and humility. We come together from diverse countries, languages, and church backgrounds to celebrate Scripture. Sarcasm, belittling, or arrogant behavior has no place in our arenas.
          </p>
        </section>

        {/* Section 2 */}
        <section className="space-y-3">
          <h2 className="text-lg sm:text-xl font-bold text-[#FAFAFA] flex items-center gap-2.5">
            <AlertOctagon className="w-5 h-5 text-red-500 shrink-0" />
            <span>2. Strictly Prohibited Conduct</span>
          </h2>
          <p className="text-sm text-[#D4D4D4] leading-relaxed">
            Quick!Bible strictly forbids the following across all usernames, profiles, community feed posts, comments, and room chats:
          </p>
          <ul className="grid grid-cols-1 sm:grid-cols-2 gap-2.5 text-xs text-[#D4D4D4] pt-1">
            <li className="p-3 rounded-xl bg-red-950/20 border border-red-900/40">
              <strong>Profanity &amp; Vulgarity:</strong> No obscene words, swearing, or disguised profane acronyms.
            </li>
            <li className="p-3 rounded-xl bg-red-950/20 border border-red-900/40">
              <strong>Hate Speech &amp; Abuse:</strong> No discriminatory, hateful, derogatory, or abusive language targeting any group or person.
            </li>
            <li className="p-3 rounded-xl bg-red-950/20 border border-red-900/40">
              <strong>Harassment &amp; Bullying:</strong> No stalking, intimidating, threatening, mocking, or personal attacks.
            </li>
            <li className="p-3 rounded-xl bg-red-950/20 border border-red-900/40">
              <strong>Sexually Explicit Content:</strong> Zero tolerance for pornography, sexually suggestive media, or nudity.
            </li>
            <li className="p-3 rounded-xl bg-red-950/20 border border-red-900/40">
              <strong>Spam &amp; Commercial Solicitation:</strong> No advertisements, chain letters, crypto scams, or phishing links.
            </li>
            <li className="p-3 rounded-xl bg-red-950/20 border border-red-900/40">
              <strong>Scripture Mockery &amp; Vandalism:</strong> No intentional desecration or blasphemous corruption of canonical Bible texts.
            </li>
          </ul>
        </section>

        {/* Section 3 */}
        <section className="space-y-3">
          <h2 className="text-lg sm:text-xl font-bold text-[#FAFAFA] flex items-center gap-2.5">
            <Sparkles className="w-5 h-5 text-[#A3A3A3] shrink-0" />
            <span>3. Profile Pictures and Avatars</span>
          </h2>
          <p className="text-sm text-[#D4D4D4] leading-relaxed">
            User profile pictures must be wholesome and family-friendly. Acceptable uploads include clean headshots, portraits, church emblems, or modest artistic graphics. Images containing violence, weapons, nudity, alcohol, drugs, or political provocation will be removed immediately.
          </p>
        </section>

        {/* Section 4 */}
        <section className="space-y-3">
          <h2 className="text-lg sm:text-xl font-bold text-[#FAFAFA] flex items-center gap-2.5">
            <MessageCircle className="w-5 h-5 text-[#A3A3A3] shrink-0" />
            <span>4. Community Feed &amp; Goal Sharing</span>
          </h2>
          <p className="text-sm text-[#D4D4D4] leading-relaxed">
            The Community Feed is designed strictly for celebrating Bible quiz milestones, study goals, and scripture insights. Posts are limited to text reflections (up to 200 characters) and allowed reactions are limited to: Like (👍), Heart (❤️), Haha (😂), Sad (😢), and Pray (🙏). External links and promotional postings are disallowed.
          </p>
        </section>

        {/* Section 5 */}
        <section className="space-y-3 border-t border-[#262626] pt-6">
          <h2 className="text-lg sm:text-xl font-bold text-[#FAFAFA] flex items-center gap-2.5">
            <Flag className="w-5 h-5 text-amber-500 shrink-0" />
            <span>5. Reporting Violations &amp; Moderation</span>
          </h2>
          <p className="text-sm text-[#D4D4D4] leading-relaxed">
            If you encounter any inappropriate username, profile picture, post, or comment, please use the <strong>Report</strong> button provided throughout the app.
          </p>
          <div className="flex flex-wrap items-center gap-3 pt-2">
            {onRequestReport && (
              <button
                type="button"
                onClick={onRequestReport}
                className="inline-flex items-center gap-1.5 px-4 py-2 rounded-xl bg-amber-500/10 border border-amber-500/30 text-xs font-semibold text-amber-300 hover:bg-amber-500/20 transition-colors"
              >
                <Flag className="w-3.5 h-3.5" />
                <span>Submit a Community Report</span>
              </button>
            )}
            <span className="text-xs text-[#737373] text-[#A3A3A3]">
              Or email: <strong>adonizchoy@gmail.com</strong>
            </span>
          </div>
        </section>
      </div>
    </div>
  );
};
